import { useState } from "react";
import { Link } from "react-router-dom";
import { useAdminUsers, type AdminUserSummary } from "@/lib/admin";
import { AppTopbar } from "@/components/AppTopbar";
import { CoursesCatalogDrawer } from "@/components/CoursesCatalogDrawer";

export default function AdminUsers() {
  const users = useAdminUsers();
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <AppTopbar onOpenDrawer={() => setDrawerOpen(true)} />
      <CoursesCatalogDrawer open={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <main className="p-8 max-w-6xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <h1 className="font-display font-extrabold tracking-tight text-3xl sm:text-4xl">Users</h1>
          <Link
            to="/admin/invitations"
            className="rounded bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:opacity-90"
          >
            Invite a user →
          </Link>
        </div>
        <p className="text-muted-foreground text-sm mb-6">All seeded user accounts.</p>
        {users.status === "loading" && <p className="text-muted-foreground">Loading…</p>}
        {users.status === "error" && (
          <p className="text-destructive">Failed to load users: {users.message}</p>
        )}
        {users.status === "ready" && (
          <div className="rounded-card border border-border overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-card/60 text-muted-foreground text-left">
                <tr>
                  <th className="px-4 py-2"></th>
                  <th className="px-4 py-2">Email</th>
                  <th className="px-4 py-2">Name</th>
                  <th className="px-4 py-2">Status</th>
                  <th className="px-4 py-2">Last login</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {users.users.map((u) => (
                  <UserRow key={u.userId} u={u} />
                ))}
                {users.users.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-4 text-muted-foreground text-center">
                      No users seeded yet.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </>
  );
}

function UserRow({ u }: { u: AdminUserSummary }) {
  const lastLogin = u.lastSignInAt ? new Date(u.lastSignInAt).toLocaleString() : "—";
  const onboarded = u.onboardingCompletedAt !== null;
  return (
    <tr className="border-t border-border-subtle">
      <td className="px-4 py-2 align-middle">
        <span
          className={`inline-block h-2.5 w-2.5 rounded-full ${u.online ? "bg-success" : "bg-muted-foreground"}`}
          aria-label={u.online ? "Online" : "Offline"}
        />
      </td>
      <td className="px-4 py-2 font-mono text-xs">{u.email}</td>
      <td className="px-4 py-2">
        <Link to={`/admin/user/${u.userId}`} className="text-primary-soft hover:underline">
          {u.name}
        </Link>
      </td>
      <td className="px-4 py-2">
        <span
          className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs ${
            onboarded ? "bg-success/15 text-success" : "bg-muted text-muted-foreground"
          }`}
        >
          <span
            className={`inline-block h-1.5 w-1.5 rounded-full ${onboarded ? "bg-success" : "bg-muted-foreground"}`}
            aria-hidden="true"
          />
          {onboarded ? "Onboarded" : "Not onboarded"}
        </span>
      </td>
      <td className="px-4 py-2 text-muted-foreground">{lastLogin}</td>
      <td className="px-4 py-2 text-right">
        <Link to={`/admin/user/${u.userId}`} className="text-xs text-muted-foreground hover:text-foreground">
          View →
        </Link>
      </td>
    </tr>
  );
}
